/**
 * Los tres estados que **toda** vista tiene que resolver: cargando, error y
 * vacío. Están acá para que ningún módulo los reinvente — y sobre todo para
 * que ninguno se los saltee.
 *
 * Un dashboard en blanco mientras carga se percibe como roto; un error sin
 * `trace_id` genera un ticket que nadie puede investigar; un "0 resultados"
 * sin explicación hace que el analista crea que perdió los datos.
 *
 * Uso normal — no hace falta escribir los tres a mano:
 *
 *     <QueryBoundary query={q} empty={{ title: t('users.empty') }}>
 *       {(data) => <UserTable users={data.items} />}
 *     </QueryBoundary>
 */
import type { ReactNode } from 'react';

import { asApiError } from '@/api/errors';
import { describeApiError } from '@/i18n/errorMessages';
import { useI18n } from '@/i18n/useI18n';
import { Button } from './Button';
import { cn } from './cn';

// ────────────────────────────────────────────────────────────────── cargando

export function Skeleton({ className }: { className?: string }): ReactNode {
  return (
    <div
      className={cn('animate-pulse rounded bg-border/60', className)}
      // Decorativo: el estado ya lo anuncia `LoadingState`. Sin esto un
      // lector de pantalla lee doce cajas vacías.
      aria-hidden="true"
    />
  );
}

export function LoadingState({
  rows = 4,
  label,
  className,
}: {
  rows?: number;
  label?: string;
  className?: string;
}): ReactNode {
  const { t } = useI18n();
  return (
    // `aria-busy` + `role="status"`: el lector anuncia "cargando" una vez y
    // después anuncia el contenido, sin leer el esqueleto.
    <div role="status" aria-busy="true" aria-live="polite" className={cn('space-y-3 p-5', className)}>
      <span className="sr-only">{label ?? t('state.loading')}</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className={index === 0 ? 'h-5 w-1/3' : 'h-4 w-full'} />
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────── error

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}): ReactNode {
  const { t } = useI18n();
  const described = describeApiError(asApiError(error), t);

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-start gap-3 rounded-(--radius-card) border border-critical/30 bg-critical-soft p-5',
        className,
      )}
    >
      <div>
        <p className="text-sm font-semibold text-critical">{t('state.error.title')}</p>
        <p className="mt-1 text-sm text-ink">{described.title}</p>
        {described.hint !== null && (
          <p className="mt-1 text-sm text-ink-muted">{described.hint}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {onRetry !== undefined && described.retriable && (
          <Button size="sm" variant="secondary" onClick={onRetry}>
            {t('state.error.retry')}
          </Button>
        )}
        {described.traceId !== null && (
          <p className="text-xs text-ink-subtle">
            {t('state.error.trace')}:{' '}
            <code className="font-mono select-all">{described.traceId}</code>
          </p>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────── vacío

export function EmptyState({
  title,
  detail,
  action,
  className,
}: {
  title?: string;
  detail?: string;
  action?: ReactNode;
  className?: string;
}): ReactNode {
  const { t } = useI18n();
  return (
    <div className={cn('flex flex-col items-center gap-2 px-5 py-12 text-center', className)}>
      <p className="text-sm font-medium text-ink">{title ?? t('state.empty.title')}</p>
      <p className="max-w-prose text-sm text-ink-muted">{detail ?? t('state.empty.detail')}</p>
      {action !== undefined && <div className="mt-2">{action}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────── el envoltorio

/** Lo mínimo de una query de TanStack que necesita `QueryBoundary`. */
export interface BoundaryQuery<TData> {
  data: TData | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch?: () => unknown;
}

export interface QueryBoundaryProps<TData> {
  query: BoundaryQuery<TData>;
  children: (data: TData) => ReactNode;
  /** Textos del estado vacío. */
  empty?: { title?: string; detail?: string; action?: ReactNode };
  /**
   * ¿El resultado cuenta como vacío? Por defecto: array sin elementos o
   * `{ items: [] }`. Pasala cuando la forma sea otra.
   */
  isEmpty?: (data: TData) => boolean;
  loadingRows?: number;
}

function defaultIsEmpty(data: unknown): boolean {
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object' && data !== null && 'items' in data) {
    const { items } = data;
    return Array.isArray(items) && items.length === 0;
  }
  return false;
}

/**
 * Decide entre cargando / error / vacío / contenido. Es el único lugar donde
 * está escrito ese orden, y el orden importa: si hay error se muestra el
 * error aunque haya datos viejos en cache, porque un dato viejo presentado
 * como actual es peor que un error visible.
 */
export function QueryBoundary<TData>({
  query,
  children,
  empty,
  isEmpty = defaultIsEmpty,
  loadingRows,
}: QueryBoundaryProps<TData>): ReactNode {
  if (query.isPending) {
    return <LoadingState {...(loadingRows === undefined ? {} : { rows: loadingRows })} />;
  }
  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        {...(query.refetch === undefined
          ? {}
          : {
              onRetry: () => {
                query.refetch?.();
              },
            })}
      />
    );
  }
  if (query.data === undefined) return <EmptyState {...empty} />;
  if (isEmpty(query.data)) return <EmptyState {...empty} />;
  return <>{children(query.data)}</>;
}
